import usePublicUserSMS from "@app-contexts/public/features/checkout/data/services/usePublicUserSMS";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import FormControl, {
  FormErrorMessage,
  FormLabel,
} from "@ui-kit/form/FormControl/FormControl.web";
import { yupResolver } from "@hookform/resolvers/yup";
import Card from "@shared/components/ui-kit/data-display/Card/Card.web";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import Form from "@shared/components/ui-kit/form/Form/Form.web";
import PhoneNumberInput from "@shared/components/ui-kit/form/PhoneNumberInput/PhoneNumberInput.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import CDNImage from "@shared/components/ui-kit/media/CDNImage/CDNImage.web";
import Modal from "@shared/components/ui-kit/overlays/Modal/Modal.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import {
  getCountryCodeFromPhoneInput,
  PhoneCountryType,
} from "@shared/lib/libphonenumber/api";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { useTranslation } from "@shared/utils/internationalization";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  SMSDeliveryFormData,
  SMSDeliveryFormSharedProps,
} from "./SMSDelivery.props";
import { smsDeliverySchema } from "./utils/schema";

function SMSDeliveryHeader() {
  const { t } = useTranslation("checkout");

  return (
    <>
      <Box alignItems="center" mb="md">
        <CDNImage
          src={formatRelativeStaticPath("images/checkout/text-the-ticket.png")}
          alt="text-the-ticket"
          width={344}
          height={127}
        />
      </Box>
      <Box mb="md">
        <Text fontSize="xl" fontWeight="bold">
          {t("Send tickets to your mobile phone")}
        </Text>
        <Text fontSize="md" color="gray.500" w="100%">
          {t("Your order is saved securely to your account.")}
        </Text>
      </Box>
    </>
  );
}
function SMSDeliveryForm({
  phoneNumber,
  onPhoneChange,
  onSendClick,
  textSent,
}: {
  phoneNumber: string | null;
  onPhoneChange: (value: string, country: PhoneCountryType) => void;
  onSendClick: () => void;
  textSent: boolean;
}) {
  const { t } = useTranslation("checkout");
  const [phoneCountry, setPhoneCountry] = useState<PhoneCountryType>({
    countryCode: getCountryCodeFromPhoneInput(phoneNumber || ""),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SMSDeliveryFormData>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - types are correct for yupResolver
    resolver: yupResolver(smsDeliverySchema(phoneCountry)),
  });

  return (
    <Box mb="md" data-testid="sms-delivery-form">
      <Form onSubmit={handleSubmit(onSendClick)} data-sentry-mask>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <FormControl isInvalid={Boolean(errors.phone)}>
              <FormLabel htmlFor="phone">{t("Enter phone number")}</FormLabel>
              <PhoneNumberInput
                {...field}
                id="phone-number-input"
                data-testid="phone-number-input"
                placeholder={t("Phone Number")}
                handleSetPhone={(value, country) => {
                  field.onChange(value);
                  setPhoneCountry(country);
                  onPhoneChange(value, country);
                }}
                phoneInput={phoneNumber || ""}
                countryInput={phoneCountry}
              />
              {!textSent && (
                <FormErrorMessage>{errors.phone?.message}</FormErrorMessage>
              )}
            </FormControl>
          )}
        />
        <Button.Outline
          data-testid="order-status-deliver-tickets"
          w="100%"
          mt="md"
          type="submit"
          isDisabled={textSent}
        >
          {textSent ? t("Sent") : t("Send")}
        </Button.Outline>
      </Form>
    </Box>
  );
}

function SMSDeliveryModal({
  isOpen,
  onClose,
  phoneNumber,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string | null;
  onConfirm: () => void;
}) {
  const { t } = useTranslation("checkout");

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <Modal.Header onClose={onClose} borderBottom="none">
        {t("Confirmation")}
      </Modal.Header>
      <Modal.Body>
        <Flex direction="column" alignItems="center" gap="md">
          <Text fontSize="sm">
            {t("Please confirm the phone number below is correct")}
          </Text>
          <Text fontWeight="bold" fontSize="md">
            {phoneNumber}
          </Text>
          <Button
            w="100%"
            variant="solid"
            onClick={onConfirm}
            data-testid="confirm-phone-number-button"
          >
            {t("Confirm")}
          </Button>
        </Flex>
      </Modal.Body>
    </Modal>
  );
}

function SMSDelivery({
  uuid,
  uuid2,
  transactionId,
  onUpdateSMSSettings,
}: SMSDeliveryFormSharedProps) {
  const {
    isOpen: isPhoneNumberModalOpen,
    onClose: onPhoneNumberModalClose,
    onOpen: onPhoneNumberModalOpen,
  } = useDisclosure();
  const { useDeliverTickets } = usePublicUserSMS();
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [textSent, setTextSent] = useState(false);
  const { mutate: deliverTickets } = useDeliverTickets({
    transaction_id: transactionId,
  });
  const notifier = NotifierFactory.createNotifier();

  const handleDeliverTickets = () => {
    if (phoneNumber) {
      onUpdateSMSSettings();
      deliverTickets(
        {
          uuid1: uuid,
          uuid2,
          phone_number: phoneNumber,
        },
        {
          onSuccess: () => {
            notifier.success(`Items were sent to ${phoneNumber}`);
            setTextSent(true);
          },
          onError: () => {
            notifier.error("Failed to send tickets. Please try again.");
          },
        }
      );
    }
  };

  const handleConfirm = () => {
    handleDeliverTickets();
    onPhoneNumberModalClose();
  };

  return (
    <Card>
      <Flex direction="column">
        <SMSDelivery.Header />
        <SMSDelivery.Form
          phoneNumber={phoneNumber}
          textSent={textSent}
          onPhoneChange={(value) => {
            setPhoneNumber(value);
          }}
          onSendClick={onPhoneNumberModalOpen}
        />
        <SMSDelivery.Modal
          isOpen={isPhoneNumberModalOpen}
          onClose={onPhoneNumberModalClose}
          phoneNumber={phoneNumber}
          onConfirm={handleConfirm}
        />
      </Flex>
    </Card>
  );
}

SMSDelivery.Header = SMSDeliveryHeader;
SMSDelivery.Form = SMSDeliveryForm;
SMSDelivery.Modal = SMSDeliveryModal;

export default SMSDelivery;
